/*
 * Fallen Season: "Buy 2, get 1 free" offer tracker and free True North gift (custom, not part of Broadcast).
 * Markup, messages and data come from snippets/fs-offer-tracker.liquid. Runs on product pages, in the cart drawer and on the cart page.
 *
 * Cart data: the first render uses the cart Liquid printed into the page (no request).
 * After that there is one /cart.js request per cart change. Broadcast's 'theme:cart:change' event
 * carries only an item count, not the cart, so the cart is fetched as soon as an add succeeds
 * ('theme:product:add') and the change event that follows is not fetched again.
 */
(function () {
  if (window.FSOffer) return;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const PICK_LIMIT = 6;
  const LOST_KEY = 'fsGiftLost';
  const SKIP_WINDOW = 2000;
  const offerIds = new Set();
  let offerProducts = [];
  let gift = null;
  let deliveryThreshold = 0;
  let messages = {};
  let initialCart = null;
  let busy = false;
  let pending = null;
  let again = false;
  let freshUntil = 0;

  function readData() {
    document.querySelectorAll('[data-fs-offer-data]').forEach((node) => {
      try {
        const data = JSON.parse(node.textContent);
        (data.ids || []).forEach((id) => offerIds.add(id));
        if (data.products && data.products.length) offerProducts = data.products;
        if (data.gift) gift = data.gift;
        if (data.delivery) deliveryThreshold = data.delivery;
        if (data.messages) messages = data.messages;
        if (data.cart) initialCart = data.cart;
      } catch (error) {
        console.log(error);
      }
    });
  }

  function pounds(cents) {
    const amount = cents / 100;
    return `£${Number.isInteger(amount) ? amount : amount.toFixed(2)}`;
  }

  function giftLost(value) {
    try {
      if (value === undefined) return sessionStorage.getItem(LOST_KEY) === '1';
      if (value) sessionStorage.setItem(LOST_KEY, '1');
      else sessionStorage.removeItem(LOST_KEY);
    } catch (error) {
      return false;
    }
    return value;
  }

  // Offer-collection lines only, after discounts: the total the free True North is measured against
  function offerTotal(cart) {
    return cart.items.reduce((total, item) => (offerIds.has(item.product_id) ? total + item.final_line_price : total), 0);
  }

  // Messages are edited in Theme settings > Fallen Season offer. [amount] is the £ figure still to spend.
  // Speed v5: each tracker carries its own label-built messages (data-fs-offer-messages), so the product page
  // and the drawer each use their own step labels. Falls back to the shared messages.
  function ownMessages(tracker) {
    if (!tracker) return messages;
    if (!tracker._fsMessages) {
      let own = {};
      try {
        own = JSON.parse(tracker.dataset.fsOfferMessages || '{}');
      } catch (error) {
        own = {};
      }
      tracker._fsMessages = Object.assign({}, messages, own);
    }
    return tracker._fsMessages;
  }

  function message(state, context, tracker) {
    const messages = ownMessages(tracker);
    const {count, sub, giftSub, giftIn} = state;
    const top = gift ? gift.threshold : 0;
    let text = messages.three;
    let amount = top - giftSub;

    if (giftIn) text = messages.giftIn;
    else if (gift && giftSub >= top) text = messages.unlocked;
    else if (count === 0) text = messages.none;
    else if (count === 1 && sub < deliveryThreshold) {
      text = messages.oneUnder;
      amount = deliveryThreshold - sub;
    } else if (count === 1) text = messages.oneOver;
    else if (count === 2) text = context === 'product' ? messages.twoProduct : messages.two;

    return (text || '').split('[amount]').join(pounds(Math.max(amount, 0)));
  }

  // Progress in stops: 0 = 1st piece, 1 = Free delivery, 2 = 2nd piece, 3 = 3rd FREE, 4 = Free True North
  function position(state) {
    const {count, sub, giftSub} = state;
    const top = gift ? gift.threshold : Infinity;

    if (giftSub >= top) return 4;
    if (count >= 3) return 3 + Math.min(Math.max((giftSub - deliveryThreshold) / (top - deliveryThreshold), 0), 1) * 0.9;
    if (count >= 2) return 2;
    if (deliveryThreshold > 0) return Math.min(sub / deliveryThreshold, 1);
    return 0;
  }

  function post(path, body) {
    return fetch(`${root}${path}`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify(body),
    }).then((response) => response.json().then((json) => ({ok: response.ok, body: json})));
  }

  // Ask Broadcast to re-render the drawer or cart page lines. It answers with 'theme:cart:change'.
  function redrawTheme() {
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
  }

  function buildCard(product) {
    const card = document.createElement('div');
    card.className = 'fs-offer__card';

    const imageLink = document.createElement('a');
    imageLink.className = 'fs-offer__card-image';
    imageLink.href = product.url;
    imageLink.setAttribute('aria-label', product.title);
    if (product.image) {
      const image = document.createElement('img');
      image.src = product.image;
      image.alt = '';
      image.loading = 'lazy';
      image.width = 120;
      image.height = 120;
      imageLink.appendChild(image);
    }

    const title = document.createElement('a');
    title.className = 'fs-offer__card-title';
    title.href = product.url;
    title.textContent = product.title;

    const price = document.createElement('span');
    price.className = 'fs-offer__card-price';
    price.textContent = product.variants[0].price;

    card.append(imageLink, title, price);

    let select = null;
    if (product.variants.length > 1) {
      select = document.createElement('select');
      select.className = 'fs-offer__card-select';
      select.setAttribute('aria-label', `Option for ${product.title}`);
      product.variants.forEach((variant) => {
        const option = document.createElement('option');
        option.value = variant.id;
        option.textContent = variant.title;
        option.dataset.price = variant.price;
        select.appendChild(option);
      });
      select.addEventListener('change', () => {
        price.textContent = select.selectedOptions[0].dataset.price;
      });
      card.appendChild(select);
    }

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'fs-offer__add btn btn--primary btn--outline btn--small';
    button.textContent = 'Add';
    button.setAttribute('aria-label', `Add ${product.title}`);
    button.addEventListener('click', () => {
      const error = button.closest('[data-fs-offer-pick]').querySelector('[data-fs-offer-error]');
      addToCart(select ? Number(select.value) : product.variants[0].id, button, error);
    });
    card.appendChild(button);

    return card;
  }

  function renderPick(tracker, count, cartIds) {
    const pick = tracker.querySelector('[data-fs-offer-pick]');
    if (!pick) return;

    const list = pick.querySelector('[data-fs-offer-list]');
    const products = count === 2 ? offerProducts.filter((product) => !cartIds.has(product.id) && product.variants.length).slice(0, PICK_LIMIT) : [];
    const key = products.map((product) => product.id).join(',');

    pick.hidden = products.length === 0;
    if (list.dataset.key === key) return;

    list.dataset.key = key;
    list.replaceChildren(...products.map(buildCard));
    list.scrollLeft = 0;
  }

  function renderBrowse(tracker, state) {
    const browse = tracker.querySelector('[data-fs-offer-browse]');
    if (!browse) return;

    const unlocked = gift && state.giftSub >= gift.threshold;
    browse.hidden = state.giftIn || (!unlocked && state.count === 0);
  }

  function renderGift(tracker, state) {
    const panel = tracker.querySelector('[data-fs-offer-gift]');
    if (!panel || !gift) return;

    const title = panel.querySelector('[data-fs-offer-gift-title]');
    const price = panel.querySelector('[data-fs-offer-gift-price]');
    const button = panel.querySelector('[data-fs-offer-gift-add]');
    let text = '';
    let offer = false;

    if (state.giftIn) {
      text = 'Your free True North is in.';
    } else if (state.giftSub >= gift.threshold) {
      text = 'Free True North unlocked';
      offer = true;
    } else if (state.lost && state.items > 0) {
      text = `Add ${pounds(gift.threshold - state.giftSub)} more to keep your free True North.`;
    }

    panel.hidden = !text;
    if (title.textContent !== text) title.textContent = text;
    price.hidden = !offer;
    button.hidden = !offer;
    if (offer) button.disabled = false;
  }

  // One Gift per order, and only while the discounted offer total is at or over the threshold.
  // /cart/update.js answers with the updated cart, so no further request is needed.
  function enforceGift(cart) {
    if (!gift || busy) return false;

    const lines = cart.items.filter((item) => item.variant_id === gift.variant);
    if (!lines.length) return false;

    const updates = {};
    if (offerTotal(cart) < gift.threshold) {
      lines.forEach((line) => (updates[line.key] = 0));
      giftLost(true);
    } else {
      lines.forEach((line, index) => {
        if (index > 0) updates[line.key] = 0;
        else if (line.quantity > 1) updates[line.key] = 1;
      });
    }

    if (!Object.keys(updates).length) return false;

    busy = true;
    post('cart/update.js', {updates})
      .then(({ok, body}) => {
        busy = false;
        if (ok && body.items) {
          freshUntil = Date.now() + SKIP_WINDOW;
          render(body);
        }
        redrawTheme();
      })
      .catch((error) => {
        busy = false;
        console.log(error);
      });
    return true;
  }

  function render(cart) {
    if (enforceGift(cart)) return;

    const cartIds = new Set();
    const sub = cart.total_price;
    const giftSub = offerTotal(cart);
    let count = 0;
    let giftIn = false;

    cart.items.forEach((item) => {
      cartIds.add(item.product_id);
      if (gift && item.variant_id === gift.variant) giftIn = true;
      else if (offerIds.has(item.product_id)) count += item.quantity;
    });

    if (gift && (giftSub >= gift.threshold || cart.item_count === 0)) giftLost(false);

    const state = {count, sub, giftSub, giftIn, lost: giftLost(), items: cart.item_count};
    const filled = {
      first: count >= 1,
      delivery: deliveryThreshold > 0 && sub >= deliveryThreshold,
      two: count >= 2,
      free: count >= 3,
      gift: Boolean(gift) && giftSub >= gift.threshold,
    };
    const progress = `${(position(state) / 4) * 100}%`;

    document.querySelectorAll('[data-fs-offer]').forEach((tracker) => {
      tracker.querySelector('[data-fs-offer-bar]').style.setProperty('--fs-progress', progress);
      tracker.querySelectorAll('[data-fs-offer-step]').forEach((step) => {
        step.classList.toggle('is-filled', Boolean(filled[step.dataset.fsOfferStep]));
      });

      const text = tracker.querySelector('[data-fs-offer-text]');
      const next = message(state, tracker.dataset.context, tracker);
      if (text && text.textContent !== next) text.textContent = next;

      // "Hide summary line when the bag is empty": no line, and no gap for it, until an offer piece is in the bag
      if (tracker.hasAttribute('data-fs-offer-hide-empty')) {
        const empty = count === 0;
        if (text) text.hidden = empty;
        tracker.classList.toggle('fs-offer--no-summary', empty);
      }

      renderGift(tracker, state);
      renderBrowse(tracker, state);
      renderPick(tracker, count, cartIds);
    });
  }

  // One /cart.js request at a time
  function refresh() {
    if (pending) return pending;

    pending = fetch(`${root}cart.js`, {headers: {Accept: 'application/json'}})
      .then((response) => response.json())
      .then(render)
      .catch((error) => console.log(error))
      .then(() => {
        pending = null;
        if (again) {
          again = false;
          refresh();
        }
      });
    return pending;
  }

  // An add just succeeded: fetch the cart now, and treat the 'theme:cart:change' that follows as already handled
  function refreshAfterAdd() {
    freshUntil = Date.now() + SKIP_WINDOW;
    return refresh();
  }

  function onCartChange() {
    if (Date.now() < freshUntil) {
      freshUntil = 0;
      return;
    }
    if (pending) {
      again = true;
      return;
    }
    refresh();
  }

  function showError(node, text) {
    node.textContent = text;
    node.hidden = !text;
  }

  function addToCart(variantId, button, error) {
    button.disabled = true;
    showError(error, '');

    post('cart/add.js', {items: [{id: variantId, quantity: 1}]})
      .then(({ok, body}) => {
        if (!ok) {
          button.disabled = false;
          showError(error, body.description || body.message || 'Sorry, that could not be added.');
          return;
        }

        refreshAfterAdd();
        redrawTheme();
      })
      .catch((err) => {
        button.disabled = false;
        console.log(err);
      });
  }

  function init() {
    readData();
    if (!document.querySelector('[data-fs-offer]')) return;

    document.querySelectorAll('[data-fs-offer-gift-add]').forEach((button) => {
      button.addEventListener('click', () => {
        addToCart(gift.variant, button, button.closest('[data-fs-offer-gift]').querySelector('[data-fs-offer-gift-error]'));
      });
    });

    // Collection links: close the drawer, then follow the link
    document.querySelectorAll('[data-fs-offer-link]').forEach((link) => {
      link.addEventListener('click', () => {
        const close = document.querySelector('#cart-drawer.is-open [data-cart-drawer-close]');
        if (close) close.click();
      });
    });

    if (initialCart && initialCart.items) render(initialCart);
    else refresh();

    document.addEventListener('theme:product:add', refreshAfterAdd);
    document.addEventListener('theme:cart:change', onCartChange);

    // Coming back with the browser's Back button can show an old copy of the page
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) refresh();
    });
  }

  window.FSOffer = {refresh};

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
